import { ref, computed, watch } from "vue";
import { useRoute } from "vue-router";
import posthog from "posthog-js";
import { isIdentified, resetPostHog } from "./usePostHog";

const PRODUCT_PARAM_KEY = "offering_experiment_product";
const IS_QA = import.meta.env.VITE_ENV === "QA";
const FLAG_NAME = IS_QA ? "sign-up-ctr-qa" : "sign-up-ctr";

const posthogTestGroup = ref(false);
const persistedProductParam = ref(localStorage.getItem(PRODUCT_PARAM_KEY) || null);
let initPromise = null;

export function useOfferingExperiment() {
  const route = useRoute();

  function initExperiment() {
    if (!persistedProductParam.value) return Promise.resolve();
    // Never evaluate the flag while the user is anonymous — stale onFeatureFlags
    // listeners from a previous sign-in/sign-out cycle within the same page session
    // can fire after posthog.reset() and would otherwise call getFeatureFlag() for
    // the new anonymous distinct_id before the user is identified.
    if (!isIdentified()) return Promise.resolve();
    // Cache the promise so multiple callers (MFA, PurchaseFlow) share one fetch
    // and never register duplicate onFeatureFlags listeners.
    if (initPromise) return initPromise;
    initPromise = new Promise((resolve) => {
      // reloadFeatureFlags() fires onFeatureFlags synchronously with stale cached
      // data before the network response arrives. callbackReady blocks that first
      // synchronous fire so we only act on the real post-reload result.
      let callbackReady = false;
      posthog.reloadFeatureFlags();
      posthog.onFeatureFlags(() => {
        if (!callbackReady) return;
        // persistedProductParam is nulled before posthog.reset() on logout, which
        // would otherwise re-fire this listener against an anonymous user.
        if (!persistedProductParam.value) return;
        // Secondary guard: stale listeners registered in a previous session can fire
        // after posthog.reset() creates a new anonymous identity. Skip if not identified.
        if (!isIdentified()) return;
        const flag = posthog.getFeatureFlag(FLAG_NAME);
        if (IS_QA) console.log(`PostHog feature flag '${FLAG_NAME}':`, flag);
        if (flag !== undefined) posthogTestGroup.value = flag === "test";
        resolve();
      });
      callbackReady = true;
    });
    return initPromise;
  }

  // Capture product param whenever it appears in the URL and persist it — once set
  // it won't be cleared by subsequent route changes that strip ?product=.
  const rawProductParam = computed(() => route.query.product ?? null);
  watch(
    rawProductParam,
    (val) => {
      if (val) {
        persistedProductParam.value = val;
        localStorage.setItem(PRODUCT_PARAM_KEY, val);
      }
    },
    { immediate: true }
  );

  const productParam = computed(() => persistedProductParam.value);
  const isInTestGroup = computed(() => posthogTestGroup.value && !!productParam.value);
  const showV2OfferingScreen = computed(() => isInTestGroup.value);
  const skipToAmountWithOffering = computed(
    () =>
      isInTestGroup.value &&
      (productParam.value === "regulation-d" || productParam.value === "registered-offering")
  );

  const preselectedOfferingType = computed(() => {
    if (productParam.value === "regulation-d") return "Reg D";
    if (productParam.value === "registered-offering") return "S1";
    return null;
  });

  return {
    initExperiment,
    isInTestGroup,
    productParam,
    showV2OfferingScreen,
    skipToAmountWithOffering,
    preselectedOfferingType,
  };
}

function overrideExperiment(variant) {
  posthogTestGroup.value = variant === "test";
  try {
    posthog.featureFlags.overrideFeatureFlags({ flags: { [FLAG_NAME]: variant } });
  } catch {
    // PostHog may not be initialized in non-QA environments — direct ref update is enough
  }
}

if (typeof window !== "undefined" && IS_QA) {
  window.__experiment = { override: overrideExperiment };
}

export function getProductParam() {
  return persistedProductParam.value;
}

export function clearProductParam() {
  persistedProductParam.value = null;
  localStorage.removeItem(PRODUCT_PARAM_KEY);
}

export function clearExperimentState() {
  posthogTestGroup.value = false;
  persistedProductParam.value = null;
  initPromise = null;
  localStorage.removeItem(PRODUCT_PARAM_KEY);
  resetPostHog();
}
